import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  type MenuItemProps,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { LuSearch, LuX } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  PROFILE_FONT,
  filterFieldSx,
  muiFieldSx,
} from "../../constants/layout";
import {
  REPORT_COLORS,
  REPORT_PAGE_INNER_SX,
  REPORT_PAGE_SX,
  REPORT_RADIUS,
  REPORT_SHADOW,
  REPORT_TYPO,
  REPORT_CARD_SX,
  REPORT_MENU_PAPER_SX,
} from "../CustomerReports/mf/mfReportsTheme";
import { getAIFSchemes } from "../../redux/holdings/holdingSlice";
import adminService, {
  type MarketPickPayload,
} from "../../redux/admin/adminService";

const NAVY = REPORT_COLORS.button;
const NAVY_HOVER = REPORT_COLORS.buttonHover;
const BORDER = REPORT_COLORS.border;
const MUTED = REPORT_COLORS.mutedText;
const SURFACE = REPORT_COLORS.white;
const SOFT = "#F8F7F6";
const BLACK = REPORT_COLORS.primaryText;
const SLOT_COUNT = 4;

const cardSx = {
  ...REPORT_CARD_SX,
  border: `1px solid ${BORDER}`,
};

const sectionTitleSx = {
  ...REPORT_TYPO.sectionTitle,
  mb: 0.75,
};

const sectionSubtitleSx = {
  ...REPORT_TYPO.pageSubtitle,
  mt: 0,
  mb: 2,
  maxWidth: 560,
};

const fieldLabelSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 10,
  fontWeight: 600,
  color: MUTED,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  mb: 0.75,
  display: "block",
};

const selectSx = {
  borderRadius: REPORT_RADIUS.md,
  bgcolor: SOFT,
  fontFamily: PROFILE_FONT,
  fontSize: 13,
  fontWeight: 500,
  color: BLACK,
  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
  "& .MuiSelect-select": {
    py: 1.15,
    px: 1.5,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSvgIcon-root": {
    color: MUTED,
    fontSize: 20,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover": {
    bgcolor: "#F3F2F1",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(30,58,95,0.12)",
  },
  "&.Mui-focused": {
    bgcolor: SURFACE,
    boxShadow: "0 0 0 3px rgba(30, 58, 95, 0.12)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: NAVY,
    borderWidth: 1,
  },
};

const slotSelectMenuProps = {
  PaperProps: {
    sx: {
      ...REPORT_MENU_PAPER_SX,
      mt: 0.75,
      minWidth: 220,
      overflow: "hidden",
    },
  },
  MenuListProps: {
    sx: {
      py: 0.75,
      px: 0.5,
    },
    onMouseDown: (event: React.MouseEvent) => {
      event.preventDefault();
    },
  },
  disableScrollLock: true,
  autoFocus: false,
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
  disableEnforceFocus: true,
};

const slotMenuItemSx = {
  borderRadius: REPORT_RADIUS.sm,
  mx: 0.25,
  my: 0.2,
  py: 1,
  px: 1.25,
  fontFamily: PROFILE_FONT,
  fontSize: 13,
  fontWeight: 500,
  color: BLACK,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  "&.Mui-selected": {
    bgcolor: "rgba(30, 58, 95, 0.1) !important",
    color: NAVY,
    fontWeight: 600,
  },
  "&.Mui-selected:hover": {
    bgcolor: "rgba(30, 58, 95, 0.14) !important",
  },
  "&:hover": {
    bgcolor: "rgba(30, 58, 95, 0.06)",
  },
};

const SlotSelectMenuItem = ({
  label,
  hint,
  selected,
  sx,
  ...menuItemProps
}: MenuItemProps & {
  label: string;
  hint?: string;
}) => (
  <MenuItem sx={{ ...slotMenuItemSx, ...sx }} {...menuItemProps}>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: 13,
          fontWeight: selected ? 600 : 500,
          color: selected ? NAVY : BLACK,
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {hint ? (
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 11,
            color: MUTED,
            lineHeight: 1.35,
            mt: 0.2,
          }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
    {selected ? (
      <CheckRoundedIcon sx={{ fontSize: 16, color: NAVY, flexShrink: 0 }} />
    ) : (
      <Box sx={{ width: 16, flexShrink: 0 }} />
    )}
  </MenuItem>
);

const headCellSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 11,
  fontWeight: 600,
  color: MUTED,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  borderBottom: `1px solid ${BORDER}`,
  py: 1.5,
  bgcolor: SOFT,
};

const bodyCellSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 14,
  color: BLACK,
  borderBottom: "1px solid #F5F5F5",
  py: 1.75,
};

type AssetClassKey = "none" | "equity" | "mf" | "aif" | "pms" | "unlisted";
type PeriodKey = "day" | "week" | "month";

const ASSET_CLASS_OPTIONS: {
  assetClass: AssetClassKey;
  holdingType?: number;
  label: string;
  shortLabel: string;
}[] = [
  { assetClass: "none", label: "None", shortLabel: "None" },
  { assetClass: "equity", holdingType: 4, label: "Equity", shortLabel: "Stock" },
  { assetClass: "mf", holdingType: 2, label: "Mutual Fund", shortLabel: "MF" },
  { assetClass: "aif", holdingType: 5, label: "AIF", shortLabel: "AIF" },
  { assetClass: "pms", holdingType: 6, label: "PMS", shortLabel: "PMS" },
  {
    assetClass: "unlisted",
    holdingType: 8,
    label: "Unlisted Equity",
    shortLabel: "Unlisted Equity",
  },
];

const PERIOD_OPTIONS: { value: PeriodKey; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const ASSET_CLASS_HINTS: Partial<Record<AssetClassKey, string>> = {
  none: "Leave this product empty",
  equity: "Listed stocks",
  mf: "Mutual funds",
  aif: "Alternative investment funds",
  pms: "Portfolio management services",
  unlisted: "Private / unlisted equity",
};

type SchemeRow = {
  id?: number | string;
  assetName?: string;
  schemeName?: string;
  isin?: string;
  stockSymbol?: string;
  category?: string;
};

type MarketPickRow = {
  id?: number;
  slot?: number;
  assetClass?: string;
  period?: string;
  displayLabel?: string;
  category?: string;
  masterId?: number | null;
  assetName?: string | null;
  stockSymbol?: string | null;
  isin?: string | null;
  performance?: number | null;
  documentUrl?: string | null;
};

type DraftSlot = {
  slot: number;
  assetClass: AssetClassKey;
  period: PeriodKey;
  displayLabel: string;
  selectedRow: SchemeRow | null;
  documentFile: File | null;
};

const getSchemeLabel = (row: SchemeRow) =>
  (row.assetName || row.schemeName || "").trim();

const getRowKey = (row: SchemeRow, index: number) =>
  row.id ?? `${getSchemeLabel(row)}-${index}`;

const getAssetClassMeta = (assetClass: AssetClassKey) =>
  ASSET_CLASS_OPTIONS.find((item) => item.assetClass === assetClass)!;

const buildDisplayLabel = (assetClass: AssetClassKey, period: PeriodKey) => {
  if (assetClass === "none") return "";
  const shortLabel = getAssetClassMeta(assetClass).shortLabel;
  const periodLabel =
    PERIOD_OPTIONS.find((item) => item.value === period)?.label || "Day";
  return `${shortLabel} of the ${periodLabel}`;
};

const isPdfFile = (file: File) => {
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
};

const downloadBase64File = (base64: string, fileName: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: "application/pdf",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "market-pick.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const renderAssetClassValue = (value: unknown) =>
  getAssetClassMeta((value as AssetClassKey) || "none").label;

const renderPeriodValue = (value: unknown) =>
  PERIOD_OPTIONS.find((item) => item.value === value)?.label || "Day";

const normalizeAssetClass = (value?: string | null): AssetClassKey | null => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw === "stock" || raw === "equity") return "equity";
  if (raw === "mutual fund" || raw === "mf") return "mf";
  if (raw === "aif") return "aif";
  if (raw === "pms") return "pms";
  if (raw === "unlisted" || raw === "unlisted equity") return "unlisted";
  return null;
};

const normalizePeriod = (value?: string | null): PeriodKey => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "week" || raw === "month") return raw;
  return "day";
};

const createEmptyDraftSlots = (): DraftSlot[] =>
  Array.from({ length: SLOT_COUNT }, (_, index) => ({
    slot: index + 1,
    assetClass: "none",
    period: "day",
    displayLabel: "",
    selectedRow: null,
    documentFile: null,
  }));

const isSlotComplete = (slot: DraftSlot) => {
  if (slot.assetClass === "none") return false;
  const masterId = Number(slot.selectedRow?.id);
  return Boolean(slot.selectedRow) && Number.isFinite(masterId) && masterId > 0;
};

const slotSwitcherButtonSx = (isActive: boolean, isComplete: boolean) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.25,
  py: 0.65,
  borderRadius: REPORT_RADIUS.pill,
  border: "1px solid",
  borderColor: isActive ? NAVY : isComplete ? "rgba(22,163,74,0.35)" : BORDER,
  bgcolor: isActive ? "rgba(30, 58, 95, 0.08)" : SURFACE,
  color: isActive ? NAVY : BLACK,
  fontFamily: PROFILE_FONT,
  fontSize: 12,
  fontWeight: isActive ? 600 : 500,
  cursor: "pointer",
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: NAVY,
    bgcolor: isActive ? "rgba(30, 58, 95, 0.12)" : "rgba(30, 58, 95, 0.04)",
  },
});

const SlotSwitcherBar = ({
  slots,
  activeSlot,
  onSelect,
}: {
  slots: DraftSlot[];
  activeSlot: number;
  onSelect: (slot: number) => void;
}) => {
  if (slots.length <= 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: MUTED,
          mr: 0.25,
        }}
      >
        Switch product
      </Typography>
      {slots.map((slot) => {
        const isActive = slot.slot === activeSlot;
        const isComplete = isSlotComplete(slot);
        const classLabel = getAssetClassMeta(slot.assetClass).shortLabel;
        const pickedName = slot.selectedRow ? getSchemeLabel(slot.selectedRow) : "";

        return (
          <Box
            key={slot.slot}
            component="button"
            type="button"
            onClick={() => onSelect(slot.slot)}
            sx={slotSwitcherButtonSx(isActive, isComplete)}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: isActive ? NAVY : isComplete ? "#ECFDF3" : SOFT,
                color: isActive ? "#fff" : isComplete ? REPORT_COLORS.profit : MUTED,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {slot.slot}
            </Box>
            <Box sx={{ textAlign: "left", minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                  color: "inherit",
                  display: "block",
                  lineHeight: 1.2,
                }}
              >
                {classLabel}
              </Typography>
              {pickedName ? (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 10,
                    color: MUTED,
                    display: "block",
                    maxWidth: 140,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  {pickedName}
                </Typography>
              ) : null}
            </Box>
            {isComplete ? (
              <CheckCircleRoundedIcon
                sx={{ fontSize: 14, color: REPORT_COLORS.profit, flexShrink: 0 }}
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

const hasPickPerformance = (
  assetClass: string,
  value: number | null | undefined
) => {
  if (assetClass === "unlisted") return false;
  if (value === null || value === undefined) return false;

  const raw = String(value).trim();
  if (!raw || raw === "-" || raw === "—") return false;

  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
};

const formatPickPerformance = (
  assetClass: string,
  value: number | null | undefined
) => {
  if (!hasPickPerformance(assetClass, value)) return null;
  const n = Number(value);
  const abs = Math.abs(n).toFixed(2);
  // Performance period is fixed by asset class (not the banner day/week/month label).
  if (assetClass === "aif" || assetClass === "pms") {
    return `${abs}% 1Y`;
  }
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${abs}% Day`;
};

const getAdminPerfStyle = (perf: string) => {
  if (perf === "—") {
    return { color: MUTED, bgcolor: SOFT };
  }
  if (perf.startsWith("-")) {
    return { color: REPORT_COLORS.loss, bgcolor: "#FEF2F2" };
  }
  if (perf.startsWith("+")) {
    return { color: REPORT_COLORS.profit, bgcolor: "#ECFDF3" };
  }
  return { color: BLACK, bgcolor: SOFT };
};

const SectionHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => (
  <Box sx={{ mb: subtitle ? 0 : 2 }}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {subtitle ? <Typography sx={sectionSubtitleSx}>{subtitle}</Typography> : null}
  </Box>
);

const AssetAvatar = ({ name }: { name: string }) => {
  const initial = (name?.trim()?.[0] || "A").toUpperCase();
  return (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: NAVY,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: PROFILE_FONT,
      }}
    >
      {initial}
    </Avatar>
  );
};

const MarketPicks = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [activeSlot, setActiveSlot] = useState(1);
  const [draftSlots, setDraftSlots] = useState<DraftSlot[]>(createEmptyDraftSlots);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState<SchemeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentPicks, setCurrentPicks] = useState<MarketPickRow[]>([]);
  const [loadingPicks, setLoadingPicks] = useState(false);
  const [deletingSlot, setDeletingSlot] = useState<number | null>(null);
  const [downloadingDocSlot, setDownloadingDocSlot] = useState<number | null>(null);

  const activateSlot = useCallback((slotNumber: number) => {
    setActiveSlot(slotNumber);
  }, []);

  const activeDraft = draftSlots.find((slot) => slot.slot === activeSlot);
  const activeAssetClass = activeDraft?.assetClass || "none";
  const canBrowseAssets = activeAssetClass !== "none";
  const activeHoldingType =
    getAssetClassMeta(activeAssetClass).holdingType ?? 4;
  const activeClassLabel = getAssetClassMeta(activeAssetClass).label;

  const fetchCurrentPicks = useCallback(async () => {
    setLoadingPicks(true);
    try {
      const response = await adminService.getMarketPicks();
      const data = response?.data;
      const picks = Array.isArray(data) ? data : [];
      setCurrentPicks(picks);
    } catch (error) {
      console.error("Error fetching current market picks:", error);
      setCurrentPicks([]);
    } finally {
      setLoadingPicks(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentPicks();
  }, [fetchCurrentPicks]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageIndex(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
    setPageIndex(1);
  }, [activeSlot, activeHoldingType]);

  const updateDraftSlot = (slot: number, patch: Partial<DraftSlot>) => {
    setDraftSlots((prev) =>
      prev.map((item) => {
        if (item.slot !== slot) return item;

        const nextAssetClass = patch.assetClass ?? item.assetClass;
        const nextPeriod = patch.period ?? item.period;
        const switchingToNone = nextAssetClass === "none";

        return {
          ...item,
          ...patch,
          assetClass: nextAssetClass,
          period: nextPeriod,
          selectedRow: switchingToNone ? null : (patch.selectedRow ?? item.selectedRow),
          documentFile: switchingToNone
            ? null
            : patch.documentFile !== undefined
              ? patch.documentFile
              : item.documentFile,
          displayLabel: switchingToNone
            ? ""
            : patch.displayLabel ??
              buildDisplayLabel(nextAssetClass, nextPeriod),
        };
      })
    );
  };

  const fetchSchemes = useCallback(async () => {
    if (!canBrowseAssets) {
      setRows([]);
      setTotalPages(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(
        getAIFSchemes({
          holdingType: activeHoldingType,
          search: debouncedSearch,
          pageIndex,
          pageSize,
        })
      );

      const payload = response?.payload;
      const data = payload?.data ?? payload;
      const schemeNames: SchemeRow[] = data?.schemeNames || [];
      const pages = Number(data?.totalPages || 0);

      setRows(
        schemeNames.filter((row) => {
          const name = getSchemeLabel(row);
          return Boolean(name) && name !== "-";
        })
      );
      setTotalPages(pages);
    } catch (error) {
      console.error("Error fetching market pick schemes:", error);
      setRows([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    activeHoldingType,
    canBrowseAssets,
    debouncedSearch,
    dispatch,
    pageIndex,
    pageSize,
  ]);

  useEffect(() => {
    fetchSchemes();
  }, [fetchSchemes]);

  const savedPicks = useMemo(
    () =>
      [...currentPicks].sort(
        (a, b) => Number(a.slot || 0) - Number(b.slot || 0)
      ),
    [currentPicks]
  );

  const liveProductSlots = useMemo(() => {
    const pickBySlot = new Map(
      savedPicks.map((pick) => [Number(pick.slot), pick] as const)
    );
    return Array.from({ length: SLOT_COUNT }, (_, index) => ({
      slot: index + 1,
      pick: pickBySlot.get(index + 1) || null,
    }));
  }, [savedPicks]);

  const configuredSlots = useMemo(
    () => draftSlots.filter((slot) => slot.assetClass !== "none"),
    [draftSlots]
  );

  const completeSlots = useMemo(
    () => draftSlots.filter(isSlotComplete),
    [draftSlots]
  );

  const canUpdate =
    completeSlots.length >= 1 &&
    completeSlots.length === configuredSlots.length &&
    !updating;

  const actionStatus = useMemo(() => {
    if (canUpdate) {
      const slotLabels = completeSlots.map((slot) => slot.slot).join(", ");
      return {
        text: `Ready to publish product${completeSlots.length > 1 ? "s" : ""} ${slotLabels}. Other live products stay unchanged.`,
        color: REPORT_COLORS.profit,
      };
    }
    if (
      configuredSlots.length > 0 &&
      completeSlots.length < configuredSlots.length
    ) {
      return {
        text: `Pick an asset for the remaining configured product${configuredSlots.length - completeSlots.length > 1 ? "s" : ""}`,
        color: REPORT_COLORS.realized,
      };
    }
    if (canBrowseAssets) {
      return {
        text: `Product ${activeSlot} · ${activeClassLabel}`,
        color: BLACK,
      };
    }
    return null;
  }, [
    activeClassLabel,
    activeSlot,
    canBrowseAssets,
    canUpdate,
    completeSlots.length,
    configuredSlots.length,
  ]);

  const handleUpdate = async () => {
    if (!canUpdate) return;

    const picks: MarketPickPayload[] = completeSlots.map((slot) => {
      const masterId = Number(slot.selectedRow?.id);
      const assetName = getSchemeLabel(slot.selectedRow || {});
      return {
        slot: slot.slot,
        assetClass: slot.assetClass,
        period: slot.period,
        displayLabel: slot.displayLabel,
        masterId,
        assetName,
        stockSymbol:
          slot.assetClass === "equity"
            ? slot.selectedRow?.stockSymbol || null
            : null,
        isin:
          slot.assetClass === "equity" || slot.assetClass === "mf"
            ? slot.selectedRow?.isin || null
            : null,
      };
    });

    const documents = completeSlots.reduce<Record<number, File>>((acc, slot) => {
      if (slot.documentFile) {
        acc[slot.slot] = slot.documentFile;
      }
      return acc;
    }, {});

    setUpdating(true);
    try {
      const response = await adminService.updateMarketPick({ picks, documents });

      if (response?.statusCode === 200 || response?.success) {
        toast.success(response?.message || "Market picks updated successfully");
        setDraftSlots((prev) =>
          prev.map((item) => {
            const wasPublished = completeSlots.some((slot) => slot.slot === item.slot);
            if (!wasPublished) return item;
            return {
              slot: item.slot,
              assetClass: "none",
              period: "day",
              displayLabel: "",
              selectedRow: null,
              documentFile: null,
            };
          })
        );
        if (completeSlots.some((slot) => slot.slot === activeSlot)) {
          setActiveSlot(completeSlots[0]?.slot || 1);
        }
        await fetchCurrentPicks();
      } else {
        toast.error(response?.message || "Failed to update market picks");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update market picks"
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePick = async (slotNumber: number) => {
    setDeletingSlot(slotNumber);
    try {
      const response = await adminService.deleteMarketPick(slotNumber);

      if (response?.statusCode === 200 || response?.success) {
        toast.success(response?.message || "Product removed from banner");
        await fetchCurrentPicks();
      } else {
        toast.error(response?.message || "Failed to remove product");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to remove product"
      );
    } finally {
      setDeletingSlot(null);
    }
  };

  const handleDownloadDocument = async (slotNumber: number) => {
    setDownloadingDocSlot(slotNumber);
    try {
      const response = await adminService.downloadMarketPickDocument(slotNumber);
      const file = response?.data?.file;
      const fileName =
        response?.data?.fileName || `market-pick-product-${slotNumber}.pdf`;

      if ((response?.statusCode === 200 || response?.success) && file) {
        downloadBase64File(file, fileName);
      } else {
        toast.error(response?.message || "Failed to download document");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download document"
      );
    } finally {
      setDownloadingDocSlot(null);
    }
  };

  const showSymbol = activeHoldingType === 4;
  const showIsin = activeHoldingType === 2 || activeHoldingType === 4;
  const colSpan = 2 + (showSymbol ? 1 : 0) + (showIsin ? 1 : 0);

  const totalRecords = totalPages * pageSize;
  const from = rows.length === 0 ? 0 : (pageIndex - 1) * pageSize + 1;
  const to = rows.length === 0 ? 0 : (pageIndex - 1) * pageSize + rows.length;

  return (
    <Box sx={{ ...REPORT_PAGE_SX, minHeight: "100%" }}>
      <Box sx={{ ...REPORT_PAGE_INNER_SX, maxWidth: 1200 }}>
        <Box
          onClick={() => navigate("/curations")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/curations");
            }
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            cursor: "pointer",
            color: MUTED,
            fontFamily: PROFILE_FONT,
            fontSize: 13,
            fontWeight: 500,
            outline: "none",
            transition: "color 0.15s ease",
            "&:hover": { color: NAVY },
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
          Curations
        </Box>

        <Box sx={{ mb: 3.5 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 26, sm: 30 },
              fontWeight: 700,
              letterSpacing: -0.6,
              color: BLACK,
              lineHeight: 1.15,
            }}
          >
            Market Picks
          </Typography>
          <Typography sx={{ ...REPORT_TYPO.pageSubtitle, mt: 1, maxWidth: 520 }}>
            Configure the investor app banner — up to four curated highlights.
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          <SectionHeader
            title="Live on banner"
            subtitle="Current state visible to investors on the dashboard."
          />

          {loadingPicks ? (
            <Box
              sx={{
                ...cardSx,
                display: "flex",
                justifyContent: "center",
                py: 5,
              }}
            >
              <CircularProgress size={24} sx={{ color: NAVY }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {liveProductSlots.map(({ slot, pick }) => {
                if (!pick) {
                  return (
                    <Box
                      key={`live-empty-${slot}`}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: REPORT_RADIUS.lg,
                        border: `1px dashed ${BORDER}`,
                        bgcolor: SOFT,
                        overflow: "hidden",
                        minHeight: 220,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.75,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          borderBottom: `1px solid ${BORDER}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            fontWeight: 600,
                            color: MUTED,
                          }}
                        >
                          Product {slot}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          gap: 0.75,
                        }}
                      >
                        <RadioButtonUncheckedRoundedIcon
                          sx={{ fontSize: 28, color: "#C5CBD3" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            color: MUTED,
                          }}
                        >
                          Not live
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                const assetClass =
                  normalizeAssetClass(pick.assetClass || pick.category) || "equity";
                const name = (pick.assetName || "").trim();
                const period = normalizePeriod(pick.period);
                const perf = formatPickPerformance(assetClass, pick.performance);
                const perfStyle = perf ? getAdminPerfStyle(perf) : null;
                const display =
                  assetClass === "equity"
                    ? (pick.stockSymbol || name || "—")
                    : name || "—";
                const footerLabel =
                  String(pick.displayLabel || "").trim() ||
                  buildDisplayLabel(assetClass, period);
                const hasDocument = Boolean(String(pick.documentUrl || "").trim());

                return (
                  <Box
                    key={pick.id || pick.slot}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: REPORT_RADIUS.lg,
                      border: `1px solid ${BORDER}`,
                      bgcolor: SURFACE,
                      overflow: "hidden",
                      boxShadow: REPORT_SHADOW.card,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#0A0A0A",
                        px: 1.75,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "rgba(241,235,235,0.65)",
                        }}
                      >
                        Product {slot}
                      </Typography>
                      <Box
                        sx={{
                          ml: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {hasDocument ? (
                          <IconButton
                            size="small"
                            aria-label={`Download document for product ${slot}`}
                            disabled={downloadingDocSlot === slot}
                            onClick={() => handleDownloadDocument(slot)}
                            sx={{
                              color: "rgba(241,235,235,0.85)",
                              p: 0.5,
                              "&:hover": {
                                color: "#fff",
                                bgcolor: "rgba(255,255,255,0.1)",
                              },
                            }}
                          >
                            {downloadingDocSlot === slot ? (
                              <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : (
                              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        ) : null}
                        <Box
                          sx={{
                            px: 0.9,
                            py: 0.25,
                            borderRadius: REPORT_RADIUS.pill,
                            bgcolor: "rgba(22,163,74,0.18)",
                            color: "#BFE6CE",
                            fontFamily: PROFILE_FONT,
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                          }}
                        >
                          LIVE
                        </Box>
                        <IconButton
                          size="small"
                          aria-label={`Remove product ${slot} from banner`}
                          disabled={deletingSlot === slot}
                          onClick={() => handleDeletePick(slot)}
                          sx={{
                            color: "rgba(241,235,235,0.75)",
                            p: 0.5,
                            "&:hover": {
                              color: "#FCA5A5",
                              bgcolor: "rgba(239,68,68,0.15)",
                            },
                          }}
                        >
                          {deletingSlot === slot ? (
                            <CircularProgress size={14} sx={{ color: "#FCA5A5" }} />
                          ) : (
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 152,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 15,
                          fontWeight: 600,
                          color: BLACK,
                          letterSpacing: -0.2,
                          lineHeight: 1.35,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          minHeight: 40,
                        }}
                      >
                        {display}
                      </Typography>

                      <Box
                        sx={{
                          minHeight: 36,
                          display: "flex",
                          alignItems: "center",
                          my: 1.25,
                        }}
                      >
                        {perf && perfStyle ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.1,
                              py: 0.4,
                              borderRadius: REPORT_RADIUS.pill,
                              bgcolor: perfStyle.bgcolor,
                              color: perfStyle.color,
                              fontFamily: PROFILE_FONT,
                              fontSize: 12,
                              fontWeight: 700,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {perf}
                          </Box>
                        ) : null}
                      </Box>

                      <Typography
                        sx={{
                          mt: "auto",
                          pt: 0.5,
                          fontFamily: PROFILE_FONT,
                          fontSize: 11,
                          color: MUTED,
                          letterSpacing: 0.1,
                        }}
                      >
                        {footerLabel}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3.5, borderColor: "rgba(0,0,0,0.06)" }} />

        <Box sx={{ mb: 2.5 }}>
          <SectionHeader
            title="Banner setup"
            subtitle="Configure a product below and publish to update it on the banner."
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {draftSlots.map((slot) => {
              const selectedName = slot.selectedRow
                ? getSchemeLabel(slot.selectedRow)
                : "";
              const isActive = slot.slot === activeSlot;
              const isNone = slot.assetClass === "none";
              const isComplete = isSlotComplete(slot);

              return (
                <Box
                  key={slot.slot}
                  onClick={() => activateSlot(slot.slot)}
                  sx={{
                    position: "relative",
                    p: 2,
                    borderRadius: REPORT_RADIUS.lg,
                    bgcolor: SURFACE,
                    border: "1px solid",
                    borderColor: isActive
                      ? NAVY
                      : isComplete
                        ? "rgba(22,163,74,0.35)"
                        : BORDER,
                    boxShadow: isActive ? REPORT_SHADOW.card : "none",
                    transition: "all 0.2s ease",
                    opacity: isNone && !isActive ? 0.65 : 1,
                    cursor: "pointer",
                    outline: "none",
                    "&:hover": {
                      borderColor: isActive ? NAVY : "rgba(30,58,95,0.25)",
                      boxShadow: REPORT_SHADOW.card,
                    },
                    "&:focus-visible": {
                      boxShadow: `0 0 0 3px rgba(30, 58, 95, 0.18)`,
                      borderColor: NAVY,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.75,
                    }}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 13,
                          fontWeight: 600,
                          color: BLACK,
                          letterSpacing: -0.2,
                        }}
                      >
                        Product {slot.slot}
                      </Typography>
                    </Box>
                    {isComplete ? (
                      <CheckCircleRoundedIcon
                        sx={{ fontSize: 18, color: REPORT_COLORS.profit }}
                      />
                    ) : null}
                  </Box>

                  <Box
                    sx={{ mb: isNone ? 0 : 1.25 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography component="span" sx={fieldLabelSx}>
                      Asset class
                    </Typography>
                    <FormControl fullWidth size="small" sx={muiFieldSx}>
                      <Select
                        value={slot.assetClass}
                        renderValue={renderAssetClassValue}
                        onChange={(e) => {
                          updateDraftSlot(slot.slot, {
                            assetClass: e.target.value as AssetClassKey,
                            selectedRow: null,
                          });
                          activateSlot(slot.slot);
                        }}
                        sx={selectSx}
                        MenuProps={slotSelectMenuProps}
                      >
                        {ASSET_CLASS_OPTIONS.map((option) => (
                          <SlotSelectMenuItem
                            key={option.assetClass}
                            value={option.assetClass}
                            label={option.label}
                            hint={ASSET_CLASS_HINTS[option.assetClass]}
                          />
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {!isNone ? (
                    <>
                      <Box sx={{ mb: 1.25 }} onClick={(e) => e.stopPropagation()}>
                        <Typography component="span" sx={fieldLabelSx}>
                          Period
                        </Typography>
                        <FormControl fullWidth size="small" sx={muiFieldSx}>
                          <Select
                            value={slot.period}
                            renderValue={renderPeriodValue}
                            onChange={(e) => {
                              updateDraftSlot(slot.slot, {
                                period: e.target.value as PeriodKey,
                              });
                              activateSlot(slot.slot);
                            }}
                            sx={selectSx}
                            MenuProps={slotSelectMenuProps}
                          >
                            {PERIOD_OPTIONS.map((option) => (
                              <SlotSelectMenuItem
                                key={option.value}
                                value={option.value}
                                label={option.label}
                              />
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          activateSlot(slot.slot);
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: REPORT_RADIUS.md,
                          bgcolor: selectedName ? "#F4F7FB" : "#FFFBEB",
                          border: `1px solid ${
                            selectedName
                              ? "rgba(30,58,95,0.08)"
                              : "rgba(180,83,9,0.12)"
                          }`,
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: selectedName ? "#EEF3F9" : "#FEF3C7",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            ...fieldLabelSx,
                            mb: 0.35,
                            color: selectedName ? MUTED : REPORT_COLORS.realized,
                          }}
                        >
                          {slot.displayLabel || "Banner label"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            fontWeight: 600,
                            color: selectedName ? BLACK : REPORT_COLORS.realized,
                            lineHeight: 1.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {selectedName || "Click to pick from list below"}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
                        <Typography component="span" sx={fieldLabelSx}>
                          Document (optional)
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.1,
                            borderRadius: REPORT_RADIUS.md,
                            bgcolor: SOFT,
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          <PictureAsPdfOutlinedIcon
                            sx={{ fontSize: 18, color: MUTED, flexShrink: 0 }}
                          />
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 12,
                              color: slot.documentFile ? BLACK : MUTED,
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {slot.documentFile?.name || "No PDF selected"}
                          </Typography>
                          {slot.documentFile ? (
                            <IconButton
                              size="small"
                              aria-label={`Clear PDF for product ${slot.slot}`}
                              onClick={() =>
                                updateDraftSlot(slot.slot, { documentFile: null })
                              }
                              sx={{ color: MUTED, p: 0.35 }}
                            >
                              <LuX size={14} />
                            </IconButton>
                          ) : null}
                          <Button
                            component="label"
                            size="small"
                            startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              flexShrink: 0,
                              textTransform: "none",
                              fontFamily: PROFILE_FONT,
                              fontSize: 12,
                              fontWeight: 600,
                              color: NAVY,
                              minWidth: 0,
                              px: 1,
                              py: 0.35,
                              borderRadius: REPORT_RADIUS.sm,
                              bgcolor: "rgba(30,58,95,0.08)",
                              "&:hover": {
                                bgcolor: "rgba(30,58,95,0.12)",
                              },
                            }}
                          >
                            Upload
                            <input
                              hidden
                              type="file"
                              accept="application/pdf,.pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                e.target.value = "";
                                if (!file) return;
                                if (!isPdfFile(file)) {
                                  toast.error("Only PDF files are allowed");
                                  return;
                                }
                                updateDraftSlot(slot.slot, { documentFile: file });
                                activateSlot(slot.slot);
                              }}
                            />
                          </Button>
                        </Box>
                      </Box>
                    </>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>

        {configuredSlots.length > 0 ? (
        <Box
          sx={{
            ...cardSx,
            mb: 2.5,
            p: { xs: 2, md: 2.25 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {actionStatus ? (
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 13,
                  fontWeight: actionStatus.color === BLACK ? 500 : 600,
                  color: actionStatus.color,
                  lineHeight: 1.45,
                }}
              >
                {actionStatus.text}
              </Typography>
            ) : null}
          </Box>

          <Button
            onClick={handleUpdate}
            disabled={!canUpdate}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: REPORT_RADIUS.md,
              px: 3,
              py: 1.25,
              minWidth: 168,
              bgcolor: canUpdate ? NAVY : "#D6D3D1",
              color: canUpdate ? "#ffffff" : "#78716C",
              boxShadow: canUpdate ? REPORT_SHADOW.button : "none",
              "&:hover": {
                bgcolor: canUpdate ? NAVY_HOVER : "#D6D3D1",
                boxShadow: canUpdate ? REPORT_SHADOW.buttonHover : "none",
              },
              "&.Mui-disabled": {
                bgcolor: "#E7E5E4",
                color: "#A8A29E",
              },
            }}
          >
            {updating ? (
              <CircularProgress size={18} sx={{ color: "#ffffff" }} />
            ) : (
              "Publish to banner"
            )}
          </Button>
        </Box>
        ) : null}

        {canBrowseAssets ? (
        <Box sx={{ ...cardSx, overflow: "hidden" }}>
            <>
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  borderBottom: `1px solid ${BORDER}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.75,
                  bgcolor: SOFT,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: MUTED,
                        mb: 0.35,
                      }}
                    >
                      Asset picker
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 15,
                        fontWeight: 600,
                        color: BLACK,
                        letterSpacing: -0.2,
                      }}
                    >
                      {activeClassLabel} · Product {activeSlot}
                    </Typography>
                  </Box>

                  <TextField
                  placeholder={`Search ${activeClassLabel.toLowerCase()} by name...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{
                    ...filterFieldSx,
                    minWidth: { xs: "100%", md: 300 },
                    maxWidth: { md: 360 },
                    "& .MuiOutlinedInput-root": {
                      ...((filterFieldSx as any)["& .MuiOutlinedInput-root"] || {}),
                      borderRadius: "12px",
                      bgcolor: SURFACE,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          pl: 0.5,
                          pr: 1,
                          color: MUTED,
                        }}
                      >
                        <LuSearch size={18} />
                      </Box>
                    ),
                    endAdornment: search ? (
                      <Box
                        component="button"
                        type="button"
                        aria-label="Clear search"
                        onClick={() => setSearch("")}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.25,
                          mr: 0.25,
                          border: "none",
                          background: "transparent",
                          color: MUTED,
                          cursor: "pointer",
                          borderRadius: "50%",
                          lineHeight: 0,
                        }}
                      >
                        <LuX size={16} />
                      </Box>
                    ) : null,
                  }}
                />
                </Box>

                <SlotSwitcherBar
                  slots={configuredSlots}
                  activeSlot={activeSlot}
                  onSelect={activateSlot}
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>{activeClassLabel}</TableCell>
                      {showSymbol && <TableCell sx={headCellSx}>Symbol</TableCell>}
                      {showIsin && <TableCell sx={headCellSx}>ISIN</TableCell>}
                      <TableCell
                        sx={{ ...headCellSx, width: 88, textAlign: "center" }}
                      >
                        Pick
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={colSpan}
                          sx={{
                            ...bodyCellSx,
                            textAlign: "center",
                            py: 8,
                            borderBottom: "none",
                          }}
                        >
                          <CircularProgress size={28} sx={{ color: NAVY }} />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={colSpan}
                          sx={{
                            ...bodyCellSx,
                            textAlign: "center",
                            py: 6,
                            borderBottom: "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 14,
                              color: MUTED,
                            }}
                          >
                            No {activeClassLabel.toLowerCase()} found
                            {debouncedSearch ? ` for “${debouncedSearch}”` : ""}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, index) => {
                        const name = getSchemeLabel(row) || "—";
                        const rowKey = getRowKey(row, index);
                        const activeSelected = activeDraft?.selectedRow;
                        const isSelected =
                          activeSelected != null &&
                          activeSelected.id != null &&
                          row.id != null &&
                          Number(activeSelected.id) === Number(row.id);

                        return (
                          <TableRow
                            key={String(rowKey)}
                            hover
                            onClick={() => {
                              activateSlot(activeSlot);
                              updateDraftSlot(activeSlot, {
                                selectedRow: isSelected ? null : row,
                              });
                            }}
                            sx={{
                              cursor: "pointer",
                          bgcolor: isSelected ? "#F4F7FB" : "transparent",
                          "&:hover": {
                            bgcolor: isSelected ? "#F4F7FB" : SOFT,
                          },
                              "&:last-child td": { borderBottom: "none" },
                            }}
                          >
                            <TableCell sx={bodyCellSx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <AssetAvatar name={name} />
                                <Typography
                                  sx={{
                                    fontFamily: PROFILE_FONT,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: BLACK,
                                  }}
                                >
                                  {name}
                                </Typography>
                              </Box>
                            </TableCell>
                            {showSymbol && (
                              <TableCell sx={bodyCellSx}>
                                {row.stockSymbol || "—"}
                              </TableCell>
                            )}
                            {showIsin && (
                              <TableCell sx={{ ...bodyCellSx, color: MUTED }}>
                                {row.isin || "—"}
                              </TableCell>
                            )}
                            <TableCell
                              sx={{
                                ...bodyCellSx,
                                textAlign: "center",
                                width: 88,
                              }}
                            >
                              {isSelected ? (
                                <CheckCircleRoundedIcon
                                  sx={{ fontSize: 24, color: NAVY }}
                                />
                              ) : (
                                <RadioButtonUncheckedRoundedIcon
                                  sx={{ fontSize: 24, color: "#C5CBD3" }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  borderTop: `1px solid ${BORDER}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 13,
                    color: MUTED,
                  }}
                >
                  Showing {from} to {to} of {totalRecords}{" "}
                  {activeClassLabel.toLowerCase()}s
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <FormControl size="small" sx={{ ...muiFieldSx, minWidth: 130 }}>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(1);
                      }}
                    >
                      <MenuItem value={10}>10 per page</MenuItem>
                      <MenuItem value={20}>20 per page</MenuItem>
                      <MenuItem value={50}>50 per page</MenuItem>
                    </Select>
                  </FormControl>

                  <Pagination
                    count={totalPages || 1}
                    page={pageIndex}
                    onChange={(_, page) => setPageIndex(page)}
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontFamily: PROFILE_FONT,
                        fontSize: 13,
                        color: MUTED,
                        borderRadius: "8px",
                      },
                      "& .Mui-selected": {
                        bgcolor: `${NAVY} !important`,
                        color: "#ffffff !important",
                      },
                    }}
                  />
                </Box>
              </Box>
            </>
        </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default MarketPicks;
